import { useHistory } from 'react-router-dom';
import { ProjectActions } from 'store/actionCreators';
import { useProjectDetailStore } from 'store/local/projectDetail';
import { useProject } from './useProject';
import { useCreateProjectInfoStore } from 'store/local/projectCreateinfo';
import { IPartInfo, ITooth } from 'lib/types/clinic/projectTypes';
import { useShallowAppSelector } from 'store/hooks';
import { useMemo, useState } from 'react';
import { useCreateProjectTeeth } from './useCreateProjectTeeth';
import { useCreateProjectIndicationStore } from 'store/local/projectCreateindication';
import { overlappingArrayElements } from 'lib/library';
import { Tooth } from 'lib/types/projectTypes';
import { getJawByToothNumber } from 'lib/clinic/library';

export const useProjectDetail = () => {
  // SECTION: declare server state
  const { loadedSource } = useShallowAppSelector(state => ({
    loadedSource: state.app.loadedSource,
  }));

  const history = useHistory();

  const {
    teeth,
    bridge,
    numbering,
    selectedTeethIsExpanded,
    isPhotogrammetry,
    setSelectedTeethIsExpanded,
    setReworkStatusModalOpen,
    setPrevPartnerManager,
  } = useProjectDetailStore();
  const { setIsInfoOpen, setIsIndicationOpen } = useCreateProjectInfoStore();
  const {
    setTooth: setCreateTooth,
    setIndicationAlias: setCreateIndicationAlias,
    setMaterialAlias: setCreateMaterialAlias,
    setIsTypeMore: setCreateIsTypeMore,
    setIsHasData: setCreateIsHasData,
    setIndicationTab: setCreateIndicationTab,
    setOptionTab: setCreateOptionTab,
    setTeeth: setCreateTeeth,
    setBridge: setCreateBridge,
    setNumbering: setCreateNumbering,
  } = useCreateProjectIndicationStore();

  const { projectCode, projectInfo } = useProject();
  // const { onToggleTooth, onToggleToothPhotogrammetry } = useCreateProjectTeeth();

  const isCreatedOnlineProject = useMemo(() => {
    if (!projectInfo) return;

    return loadedSource === 'local' && projectInfo?.offlineYn === 0;
  }, [projectInfo]);

  const handleCloseReworkStatusModal = () => setReworkStatusModalOpen(false);
  const handleOpenReworkStatusModal = () => setReworkStatusModalOpen(true);

  const handleChangePartnerManager = (number: number) => {
    ProjectActions.edit_project_manager_request({ projectCode, data: { employeeIdx: number } });
    setPrevPartnerManager(number);
  };

  const handleSelectedIndicationChange = (number: number) => {
    if (!selectedTeethIsExpanded) return;
    handleSetTeethExpanded(number, false);
  };

  const handleSetTeethExpanded = (number: number, isOnly?: boolean) => {
    if (!!selectedTeethIsExpanded)
      setSelectedTeethIsExpanded(
        selectedTeethIsExpanded?.map((item: any) => {
          if (item.number === number) {
            return { number: number, isExpanded: !item.isExpanded };
          } else {
            return !isOnly ? item : { number: item.number, isExpanded: false };
          }
        }),
      );
  };

  const handleEditRework = (data: number[]) => {
    ProjectActions.edit_project_rework_request({ projectCode, data: { reworkType: data } });
  };

  const handleEditProjectSync = (syncStatus: number) => {
    ProjectActions.edit_project_sync_request({
      projectCode,
      syncStatus,
      ownerSyncUid: projectInfo?.ownerSyncUid,
    });

    if (syncStatus === 2) {
      history.go(-1);
    }
  };

  const handleEditInformation = () => {
    setIsInfoOpen(true);
  };

  const handleEditIndication = (toothNumber: number) => {
    if (isCreatedOnlineProject) return;

    const currentTooth = teeth.find((tooth: ITooth) => tooth.number === toothNumber);
    const newTeeth = teeth?.reduce((acc: Tooth[], curr: Tooth, index: number) => {
      return acc.concat([{ ...curr, ...{ hasData: true } }]);
    }, []);

    if (isPhotogrammetry) {
      // onToggleToothPhotogrammetry(toothNumber);
      setCreateIndicationTab(1);
      setCreateOptionTab(0);
      if (!!currentTooth) {
        setCreateTooth({ ...currentTooth, hasData: true });
        setCreateIndicationAlias(currentTooth?.indicationAlias || '');
        setCreateMaterialAlias(currentTooth?.materialAlias || '');
        setCreateIsHasData(true);
        setCreateTeeth([...newTeeth]);
      } else {
        let newTooth: ITooth = {
          number: toothNumber,
          indicationIdx: '1',
          material: '2',
          materialAlias: 'ZI',
          implantType: 9,
          implantAbutmentMaterialIdx: 1,
          implantScanbodyScanIdx: 1,
          preparationType: 1,
          reconstructionType: '1',
          materialShade: 0,
          isLast: true,
          description: '',
          default: null,
          advanced: null,
          hasData: true,
          hasPim: true,
          pimType: 'normal',
          color: '#AE29B5',
          indicationAlias: 'ATCR',
        };
        if (teeth?.length > 0) {
          const jawTeeth = teeth.filter(
            tooth => getJawByToothNumber(tooth.number) === getJawByToothNumber(toothNumber),
          );

          newTooth = {
            ...newTooth,
            ...jawTeeth[0],
            number: toothNumber,
          };

          setCreateIsHasData(true);
        } else {
          setCreateIsHasData(false);
        }

        setCreateTooth(newTooth);
        setCreateIndicationAlias('ATCR');
        setCreateMaterialAlias('ZI');
        setCreateTeeth(
          overlappingArrayElements({ list: teeth, value: newTooth, condition: 'number' }),
        );
      }
    } else {
      if (!!currentTooth) {
        setCreateTooth({ ...currentTooth, hasData: true });
        setCreateIndicationAlias(currentTooth?.indicationAlias || '');
        setCreateMaterialAlias(currentTooth?.materialAlias || '');
        setCreateIsHasData(true);
      } else {
        setCreateTooth({ number: toothNumber, hasData: false });
        setCreateIndicationAlias('');
        setCreateMaterialAlias('');
        setCreateIsHasData(false);
      }
      setCreateTeeth([...newTeeth]);
    }
    setCreateBridge(bridge);
    setCreateNumbering(numbering);
    setIsIndicationOpen(true);
  };

  const handleDeleteTooth = (e: any, number: number) => {
    e.stopPropagation();

    const filterTeeth = teeth.filter((tooth: ITooth) => tooth.number !== number);

    const newTeeth = filterTeeth.map((tooth: ITooth) => {
      return { ...tooth, implantLibraryIdx: tooth.implantLibrary?.implantLibraryIdx };
    });

    // 치아 삭제시 bridge도 삭제
    const exceptedBridgeDeletedTeeth = bridge.filter((bridgeItem: number) => {
      const frontNumber = String(bridgeItem).slice(0, 2);
      const backNumber = String(bridgeItem).slice(2);

      return String(number) !== frontNumber && String(number) !== backNumber;
    });

    const submitData = {
      indication: {
        teeth: newTeeth,
        bridge: exceptedBridgeDeletedTeeth,
      },
    };

    if (loadedSource === 'server') {
      ProjectActions.edit_project_request({
        projectCode,
        data: {
          ...submitData,
        },
      });
    }
    if (loadedSource === 'local') {
      ProjectActions.edit_project_request({
        channel: 'updateProject',
        data: { projectCode, ...submitData },
      });
    }
  };

  return {
    isCreatedOnlineProject,
    handleSelectedIndicationChange,
    handleSetTeethExpanded,
    handleChangePartnerManager,
    handleCloseReworkStatusModal,
    handleOpenReworkStatusModal,
    handleEditRework,
    handleEditProjectSync,
    handleEditInformation,
    handleEditIndication,
    onDeleteTooth: handleDeleteTooth,
  };
};
